import React, { useState, useEffect, useCallback } from "react";
import {
  Container,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  CardSubtitle,
  FormFeedback,
  Alert,
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate, useLocation } from "react-router-dom";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import Breadcrumbs from "../components/Common/Breadcrumb";
import api from "../api";

import { avatarState } from "../Recoil/atom";
import { useSetRecoilState } from "recoil";
import "./ThemeSettings.css";

const avatarOptions = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];

const themeOptions = [
  {
    id: "default",
    name: "Default Dark",
    description: "Classic dark theme with blue accents",
    preview: {
      header: "#2a3042",
      sidebar: "#2a3042",
      accent: "#556ee6",
      content: "#f8f9fa",
      card: "#ffffff",
    },
  },
  {
    id: "ocean",
    name: "Ocean Blue",
    description: "Deep ocean blues with aqua highlights",
    preview: {
      header: "#1a2332",
      sidebar: "#1a2332",
      accent: "#00d4ff",
      content: "#e0f7ff",
      card: "#ffffff",
    },
  },
  {
    id: "forest",
    name: "Forest Green",
    description: "Natural greens inspired by forests",
    preview: {
      header: "#1e3a2e",
      sidebar: "#1e3a2e",
      accent: "#4caf50",
      content: "#e8f5e9",
      card: "#ffffff",
    },
  },
  {
    id: "sunset",
    name: "Sunset Orange",
    description: "Warm sunset colors with orange tones",
    preview: {
      header: "#2d1f1f",
      sidebar: "#2d1f1f",
      accent: "#ff6b35",
      content: "#ffe8e0",
      card: "#ffffff",
    },
  },
  {
    id: "royal",
    name: "Royal Purple",
    description: "Elegant purple with gold accents",
    preview: {
      header: "#2a1f3d",
      sidebar: "#2a1f3d",
      accent: "#9c27b0",
      content: "#f3e5f5",
      card: "#ffffff",
    },
  },
  {
    id: "crimson",
    name: "Crimson Red",
    description: "Bold red theme with dark undertones",
    preview: {
      header: "#2d1a1f",
      sidebar: "#2d1a1f",
      accent: "#dc3545",
      content: "#ffebee",
      card: "#ffffff",
    },
  },
  {
    id: "midnight",
    name: "Midnight Black",
    description: "Pure black with silver highlights",
    preview: {
      header: "#0d0d0d",
      sidebar: "#0d0d0d",
      accent: "#c0c0c0",
      content: "#1a1a1a",
      card: "#2a2a2a",
    },
  },
  {
    id: "cyber",
    name: "Cyber Neon",
    description: "Futuristic neon cyan and magenta",
    preview: {
      header: "#1a1a2e",
      sidebar: "#1a1a2e",
      accent: "#00ffff",
      content: "#0f0f1e",
      card: "#1a1a2e",
    },
  },
];

const contentStyleOptions = [
  {
    id: "light",
    name: "Light & Bright",
    description: "Clean white backgrounds with subtle shadows",
    preview: {
      background: "#f8f9fa",
      card: "#ffffff",
      text: "#2a3042",
    },
  },
  {
    id: "soft",
    name: "Soft Neutral",
    description: "Warm beige tones for comfortable reading",
    preview: {
      background: "#f5f3f0",
      card: "#faf8f5",
      text: "#3a3a3a",
    },
  },
  {
    id: "cool",
    name: "Cool Gray",
    description: "Modern gray palette with blue undertones",
    preview: {
      background: "#e8eaed",
      card: "#f1f3f4",
      text: "#2a3042",
    },
  },
  {
    id: "dark",
    name: "Dark Mode",
    description: "Easy on the eyes with dark backgrounds",
    preview: {
      background: "#1a1d23",
      card: "#2a2d35",
      text: "#e9ecef",
    },
  },
  {
    id: "midnight",
    name: "True Black",
    description: "Pure black for OLED screens and night use",
    preview: {
      background: "#000000",
      card: "#1a1a1a",
      text: "#ffffff",
    },
  },
  {
    id: "paper",
    name: "Paper Texture",
    description: "Warm paper-like background for reading",
    preview: {
      background: "#f4f1ea",
      card: "#fffef9",
      text: "#3a3530",
    },
  },
  {
    id: "theme-default",
    name: "Default Theme Match",
    description: "Content colors matching Default Dark theme",
    preview: {
      background: "#1a1d2e",
      card: "#2a3042",
      text: "#e9ecef",
    },
  },
  {
    id: "theme-ocean",
    name: "Ocean Theme Match",
    description: "Content colors matching Ocean Blue theme",
    preview: {
      background: "#0f1621",
      card: "#1a2332",
      text: "#e0f7ff",
    },
  },
  {
    id: "theme-forest",
    name: "Forest Theme Match",
    description: "Content colors matching Forest Green theme",
    preview: {
      background: "#0e2a1e",
      card: "#1e3a2e",
      text: "#e8f5e9",
    },
  },
  {
    id: "theme-sunset",
    name: "Sunset Theme Match",
    description: "Content colors matching Sunset Orange theme",
    preview: {
      background: "#1d0f0f",
      card: "#2d1f1f",
      text: "#ffe8e0",
    },
  },
  {
    id: "theme-royal",
    name: "Royal Theme Match",
    description: "Content colors matching Royal Purple theme",
    preview: {
      background: "#1a0f2d",
      card: "#2a1f3d",
      text: "#f3e5f5",
    },
  },
  {
    id: "theme-crimson",
    name: "Crimson Theme Match",
    description: "Content colors matching Crimson Red theme",
    preview: {
      background: "#1d0a0f",
      card: "#2d1a1f",
      text: "#ffebee",
    },
  },
  {
    id: "theme-midnight",
    name: "Midnight Theme Match",
    description: "Content colors matching Midnight Black theme",
    preview: {
      background: "#000000",
      card: "#0d0d0d",
      text: "#f5f5f5",
    },
  },
  {
    id: "theme-cyber",
    name: "Cyber Theme Match",
    description: "Content colors matching Cyber Neon theme",
    preview: {
      background: "#0a0a1e",
      card: "#1a1a2e",
      text: "#e0ffff",
    },
  },
];

const Settings = () => {
  document.title = "Pokemon Radiance | Settings";
  const history = useNavigate();
  const location = useLocation();

  // AVATAR
  const [selectedAvatar, setSelectedAvatar] = useState(null);
  const setAvatar = useSetRecoilState(avatarState);

  // THEME
  const [selectedTheme, setSelectedTheme] = useState(
    localStorage.getItem("appTheme") || "default"
  );

  // CONTENT STYLE
  const [selectedContentStyle, setSelectedContentStyle] = useState(
    localStorage.getItem("contentStyle") || "theme-default"
  );

  // CONTROL TYPE (Analog Stick vs D-Pad)
  const [controlType, setControlType] = useState(
    localStorage.getItem("controlType") || "dpad"
  );

  // DASHBOARD WIDGETS
  const [dashboardWidgets, setDashboardWidgets] = useState(() => {
    const defaultWidgets = {
      playerStats: true,
      party: false,
      box: false,
      radiantIsle: true,
      weeklyMissions: true,
      lastActive: true,
      manageItems: false,
      notifications: false
    };
    
    const saved = localStorage.getItem("dashboardWidgets");
    if (saved) {
      const parsedWidgets = JSON.parse(saved);
      // Merge with defaults to ensure new widgets are included
      return { ...defaultWidgets, ...parsedWidgets };
    }
    return defaultWidgets;
  });

  // DASHBOARD WIDGET ORDER
  const [widgetOrder, setWidgetOrder] = useState(() => {
    const defaultOrder = [
      'playerStats',
      'radiantIsle',
      'party',
      'box',
      'weeklyMissions',
      'lastActive',
      'manageItems',
      'notifications'
    ];
    
    const saved = localStorage.getItem("widgetOrder");
    if (saved) {
      const parsedOrder = JSON.parse(saved);
      // Add any missing widgets from defaults
      const missingWidgets = defaultOrder.filter(widget => !parsedOrder.includes(widget));
      return [...parsedOrder, ...missingWidgets];
    }
    return defaultOrder;
  });

  // EMAIL / PASSWORD POPUP
  const [showPopup, setShowPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [popupColor, setPopupColor] = useState("");

  const showTemporaryPopup = (message, color = "success") => {
    const colorMap = {
      green: "success",
      "#1d222e": "success",
      red: "danger",
    };
    setPopupMessage(message);
    setPopupColor(colorMap[color] || color);
    setShowPopup(true);
    setTimeout(() => setShowPopup(false), 3000);
  };

  // DISCORD LINKING
  const [discordUser, setDiscordUser] = useState(null);
  const CLIENT_ID = process.env.REACT_APP_DISCORD_CLIENT_ID;
  const REDIRECT_URI = process.env.REACT_APP_DISCORD_REDIRECT_URI
  const SCOPES = process.env.REACT_APP_DISCORD_SCOPES || "identify%20email";

  // 1️⃣ Fetch link status on mount
  useEffect(() => {
    api
      .get("/v1/discord/status", { withCredentials: true })
      .then(res => {
        if (res.data.connected) {
          setDiscordUser(res.data.user); // { id, username, discriminator, avatarUrl }
        }
      })
      .catch(() => {
        /* ignore if not linked */
      });
  }, []);


  // 2️⃣ Handle OAuth2 callback (?code=)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const code = params.get("code");
    if (code) {
      // exchange code
      api
        .post(
          "/v1/discord/link",
          { code },
          { withCredentials: true }
        )
        .then(res => {
          setDiscordUser(res.data.user);
          showTemporaryPopup("Discord connected!", "green");
        })
        .catch(err => {
          showTemporaryPopup(
            err.response?.data?.detail || "Discord link failed.",
            "red"
          );
        })
        .finally(() => {
          // clean the URL
          params.delete("code");
          const newQuery = params.toString();
          history(
            `${location.pathname}${newQuery ? "?" + newQuery : ""}`,
            { replace: true }
          );
        });
    }
  }, [location.search, history]);

  const handleDiscordConnect = () => {
    window.location.href = 
      `https://discord.com/api/oauth2/authorize` +
      `?client_id=${CLIENT_ID}` +
      `&redirect_uri=${REDIRECT_URI}` +
      `&response_type=code` +
      `&scope=${SCOPES}`;
  };

  const handleDiscordDisconnect = () => {
    api
      .post("/v1/discord/unlink", {}, { withCredentials: true })
      .then(() => {
        setDiscordUser(null);
        showTemporaryPopup("Discord disconnected.", "#1d222e");
      })
      .catch(() => {
        showTemporaryPopup("Failed to disconnect.", "red");
      });
  };

  // EMAIL FORM
  const emailValidation = useFormik({
    initialValues: { new_email: "", password: "" },
    validationSchema: Yup.object({
      new_email: Yup.string().email("Invalid email").required("Required"),
      password: Yup.string().required("Current password is required"),
    }),
    onSubmit: async (values, { resetForm }) => {
      try {
        await api.put("/v1/update-email", values, { withCredentials: true });
        showTemporaryPopup("Email updated successfully!", "#1d222e");
        resetForm();
      } catch (err) {
        showTemporaryPopup(
          err.response?.data?.detail || "Something went wrong.",
          "red"
        );
      }
    },
  });

  // PASSWORD FORM
  const passwordValidation = useFormik({
    initialValues: { old_password: "", new_password: "", confirm_password: "" },
    validationSchema: Yup.object({
      old_password: Yup.string().required("Current password is required"),
      new_password: Yup.string()
        .min(7, "Min 7 characters")
        .max(20, "Max 20 characters")
        .matches(/[A-Z]/, "Must include uppercase")
        .matches(/[^a-zA-Z0-9]/, "Must include special character")
        .required("Required"),
      confirm_password: Yup.string()
        .oneOf([Yup.ref("new_password"), null], "Passwords must match")
        .required("Required"),
    }),
    onSubmit: async (values, { resetForm }) => {
      try {
        await api.put(
          "/v1/update-password",
          {
            old_password: values.old_password,
            new_password: values.new_password,
          },
          { withCredentials: true }
        );
        showTemporaryPopup("Password updated successfully!", "#1d222e");
        resetForm();
      } catch (err) {
        showTemporaryPopup(
          err.response?.data?.detail || "Something went wrong.",
          "red"
        );
      }
    },
  });

  // AVATAR UPDATE
  const handleAvatarUpdate = async () => {
    if (selectedAvatar === null) {
      showTemporaryPopup("Please select an avatar.", "red");
      return;
    }
    try {
      await api.put(
        "/v1/update-avatar",
        { avatar: selectedAvatar },
        { withCredentials: true }
      );
      showTemporaryPopup("Avatar updated successfully!", "#1d222e");
      setAvatar(selectedAvatar);
    } catch (err) {
      showTemporaryPopup(
        err.response?.data?.detail || "Failed to update avatar.",
        "red"
      );
    }
  };

  // THEME UPDATE
  const handleThemeUpdate = async (themeId) => {
    setSelectedTheme(themeId);
    localStorage.setItem("appTheme", themeId);
    
    // Apply theme to document root
    document.documentElement.setAttribute("data-theme", themeId);
    
    // Save to backend
    const success = await saveSettingsToBackend({ theme: themeId });
    if (success) {
      showTemporaryPopup("Theme updated successfully!", "#1d222e");
    }
  };

  // CONTENT STYLE UPDATE
  const handleContentStyleUpdate = async (styleId) => {
    setSelectedContentStyle(styleId);
    localStorage.setItem("contentStyle", styleId);
    
    // Apply content style to document root
    document.documentElement.setAttribute("data-content-style", styleId);
    
    // Save to backend
    const success = await saveSettingsToBackend({ contentStyle: styleId });
    if (success) {
      showTemporaryPopup("Content style updated successfully!", "#1d222e");
    }
  };

// CONTROL TYPE UPDATE
const handleControlTypeUpdate = async (type) => {
  setControlType(type);
  localStorage.setItem("controlType", type);
  
  // Save to backend
  const success = await saveSettingsToBackend({ controlType: type });
  if (success) {
    showTemporaryPopup(`Control type changed to ${type === 'analog' ? 'Analog Stick' : 'D-Pad'}!`, "#1d222e");
  }
};

// DASHBOARD WIDGETS UPDATE
const handleWidgetToggle = async (widgetName) => {
  const newWidgets = {
    ...dashboardWidgets,
    [widgetName]: !dashboardWidgets[widgetName]
    };
    setDashboardWidgets(newWidgets);
    localStorage.setItem("dashboardWidgets", JSON.stringify(newWidgets));
    
    // Save to backend
    const success = await saveSettingsToBackend({ dashboardWidgets: newWidgets });
    
    const widgetLabels = {
      playerStats: "Player Stats Widget",
      party: "Party Widget",
      box: "Pokémon Box Widget",
      radiantIsle: "Radiant Isle Widget",
      weeklyMissions: "Weekly Missions Widget",
      lastActive: "Last Active Users Widget",
      manageItems: "Manage Items Widget",
      notifications: "Notifications Widget"
    };
    const status = newWidgets[widgetName] ? "enabled" : "disabled";
    if (success) {
      showTemporaryPopup(`${widgetLabels[widgetName]} ${status}!`, "#1d222e");
    }
  };

  // WIDGET ORDER UPDATE
  const moveWidget = (widgetName, direction) => {
    const currentIndex = widgetOrder.indexOf(widgetName);
    if (currentIndex === -1) return;
    
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= widgetOrder.length) return;
    
    const newOrder = [...widgetOrder];
    [newOrder[currentIndex], newOrder[newIndex]] = [newOrder[newIndex], newOrder[currentIndex]];
    
    setWidgetOrder(newOrder);
    localStorage.setItem("widgetOrder", JSON.stringify(newOrder));
    showTemporaryPopup(`Widget order updated!`, "#1d222e");
  };

  // DRAG AND DROP HANDLER
  const handleOnDragEnd = (result) => {
    // Check if the drag was cancelled or dropped outside
    if (!result.destination) {
      return;
    }

    // Check if the item was dropped in the same position
    if (result.destination.index === result.source.index) {
      return;
    }

    try {
      const items = Array.from(widgetOrder);
      
      // Validate indices
      if (result.source.index < 0 || result.source.index >= items.length ||
          result.destination.index < 0 || result.destination.index >= items.length) {
        console.error('Invalid drag indices');
        return;
      }

      // Perform the reorder
      const [reorderedItem] = items.splice(result.source.index, 1);
      items.splice(result.destination.index, 0, reorderedItem);

      // Update state and localStorage
      setWidgetOrder(items);
      localStorage.setItem("widgetOrder", JSON.stringify(items));
      
      // Save to backend
      saveSettingsToBackend({ widgetOrder: items }).then(success => {
        if (success) {
          showTemporaryPopup("Widget order updated!", "#1d222e");
        }
      });
    } catch (error) {
      console.error('Error during drag and drop:', error);
      showTemporaryPopup("Failed to update widget order", "red");
    }
  };

  // BACKEND SETTINGS FUNCTIONS
  const loadSettingsFromBackend = useCallback(async () => {
    try {
      const response = await api.get("/v1/user-settings", { withCredentials: true });
      const settings = response.data;
      
      // Update local state
      setSelectedTheme(settings.theme);
      setSelectedContentStyle(settings.contentStyle);
      setControlType(settings.controlType);
      setDashboardWidgets(settings.dashboardWidgets);
      setWidgetOrder(settings.widgetOrder);
      
      // Apply theme and content style to DOM
      document.documentElement.setAttribute("data-theme", settings.theme);
      document.documentElement.setAttribute("data-content-style", settings.contentStyle);
      
      // Update localStorage as backup
      localStorage.setItem("appTheme", settings.theme);
      localStorage.setItem("contentStyle", settings.contentStyle);
      localStorage.setItem("controlType", settings.controlType);
      localStorage.setItem("dashboardWidgets", JSON.stringify(settings.dashboardWidgets));
      localStorage.setItem("widgetOrder", JSON.stringify(settings.widgetOrder));
      
    } catch (error) {
      console.error("Failed to load settings from backend:", error);
      
      // If this is a new user with no settings, create default settings
      if (error.response?.status === 404 || error.response?.status === 500) {
        console.log("Creating default settings for new user...");
        const defaultSettings = {
          theme: "default",
          contentStyle: "theme-default",
          controlType: "dpad",
          dashboardWidgets: {
            playerStats: true,
            party: false,
            box: false,
            radiantIsle: true,
            weeklyMissions: true,
            lastActive: true,
            manageItems: false,
            notifications: false
          },
          widgetOrder: [
            "playerStats", "radiantIsle", "party", "box",
            "weeklyMissions", "lastActive", "manageItems", "notifications"
          ]
        };
        
        // Try to save default settings to backend
        try {
          await api.put("/v1/user-settings", defaultSettings, { withCredentials: true });
          
          // Update local state with defaults
          setSelectedTheme(defaultSettings.theme);
          setSelectedContentStyle(defaultSettings.contentStyle);
          setControlType(defaultSettings.controlType);
          setDashboardWidgets(defaultSettings.dashboardWidgets);
          setWidgetOrder(defaultSettings.widgetOrder);
          
          // Apply theme to DOM
          document.documentElement.setAttribute("data-theme", defaultSettings.theme);
          document.documentElement.setAttribute("data-content-style", defaultSettings.contentStyle);
          
          // Update localStorage
          localStorage.setItem("appTheme", defaultSettings.theme);
          localStorage.setItem("contentStyle", defaultSettings.contentStyle);
          localStorage.setItem("controlType", defaultSettings.controlType);
          localStorage.setItem("dashboardWidgets", JSON.stringify(defaultSettings.dashboardWidgets));
          localStorage.setItem("widgetOrder", JSON.stringify(defaultSettings.widgetOrder));
          
          console.log("Default settings created successfully");
          return;
        } catch (saveError) {
          console.error("Failed to save default settings:", saveError);
        }
      }
      
      // Fall back to localStorage and apply default theme if backend fails
      const fallbackTheme = localStorage.getItem("appTheme") || "default";
      const fallbackContentStyle = localStorage.getItem("contentStyle") || "theme-default";
      
      // Apply fallback theme to DOM
      document.documentElement.setAttribute("data-theme", fallbackTheme);
      document.documentElement.setAttribute("data-content-style", fallbackContentStyle);
      
      showTemporaryPopup("Using local settings - backend unavailable", "red");
    }
  }, []);

  const saveSettingsToBackend = useCallback(async (settingsUpdate) => {
    try {
      await api.put("/v1/user-settings", settingsUpdate, { withCredentials: true });
      
      // Dispatch custom event to notify other components that settings changed
      window.dispatchEvent(new CustomEvent('userSettingsChanged', { 
        detail: settingsUpdate 
      }));
      
      return true;
    } catch (error) {
      console.error("Failed to save settings to backend:", error);
      showTemporaryPopup("Failed to save settings to server", "red");
      return false;
    }
  }, []);

  // Load settings from backend on component mount
  useEffect(() => {
    loadSettingsFromBackend();
  }, [loadSettingsFromBackend]);

  // WIDGET CONFIGURATION
  const widgetConfig = {
    playerStats: {
      icon: "ri-user-star-line",
      title: "Player Stats",
      description: "Show your profile, currency, levels, and rankings"
    },
    party: {
      icon: "ri-team-line", 
      title: "Your Party",
      description: "Display your active Pokémon party on the dashboard"
    },
    box: {
      icon: "ri-inbox-line",
      title: "Pokémon Box", 
      description: "Preview your Pokémon collection with search"
    },
    radiantIsle: {
      icon: "ri-map-2-line",
      title: "Radiant Isle",
      description: "Quick access to the mysterious Radiant Isle"
    },
    weeklyMissions: {
      icon: "ri-task-line",
      title: "Weekly Missions",
      description: "Show your weekly mission progress and rewards"
    },
    lastActive: {
      icon: "ri-user-line",
      title: "Last Active Users",
      description: "View recently active players in the community"
    },
    manageItems: {
      icon: "ri-shopping-bag-line",
      title: "Manage Items",
      description: "Quick access to your item inventory"
    },
    notifications: {
      icon: "ri-notification-line",
      title: "Notifications",
      description: "View recent notifications and alerts"
    }
  };

  // RENDER WIDGET CARD
  const renderWidgetCard = (widgetKey, index) => {
    const widget = widgetConfig[widgetKey];
    const isEnabled = dashboardWidgets[widgetKey];

    const handleToggleClick = (e) => {
      e.stopPropagation();
      handleWidgetToggle(widgetKey);
    };

    return (
      <Draggable key={widgetKey} draggableId={widgetKey} index={index}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.draggableProps}
            style={{
              ...provided.draggableProps.style,
              border: isEnabled ? '2px solid var(--theme-accent)' : '2px solid #dee2e6',
              borderRadius: '12px',
              padding: '20px',
              backgroundColor: isEnabled ? 'rgba(var(--theme-accent-rgb), 0.1)' : 'transparent',
              boxShadow: snapshot.isDragging ? '0 8px 16px rgba(0,0,0,0.15)' : 'none',
              marginBottom: '16px',
              userSelect: 'none',
              position: 'relative'
            }}
          >
            {/* Drag Handle */}
            <div 
              {...provided.dragHandleProps}
              style={{
                position: 'absolute',
                top: '8px',
                left: '8px',
                cursor: 'grab',
                padding: '4px',
                borderRadius: '4px',
                backgroundColor: 'rgba(0,0,0,0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              title="Drag to reorder"
            >
              <i className="ri-drag-move-line" style={{ fontSize: '14px', color: '#666' }} />
            </div>

            {/* Toggle Switch */}
            <div 
              style={{
                position: 'absolute',
                top: '8px',
                right: '8px',
                cursor: 'pointer'
              }}
              onClick={handleToggleClick}
              title="Click to toggle"
            >
              <div style={{
                width: '40px',
                height: '20px',
                backgroundColor: isEnabled ? 'var(--theme-accent)' : '#ccc',
                borderRadius: '10px',
                position: 'relative',
                transition: 'background-color 0.3s ease'
              }}>
                <div style={{
                  width: '16px',
                  height: '16px',
                  backgroundColor: '#fff',
                  borderRadius: '50%',
                  position: 'absolute',
                  top: '2px',
                  left: isEnabled ? '22px' : '2px',
                  transition: 'left 0.3s ease',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                }} />
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px', marginTop: '8px' }}>
              <h6 style={{ margin: 0, fontWeight: '600' }}>
                <i className={widget.icon} style={{ marginRight: '8px' }} />
                {widget.title}
              </h6>
            </div>
            
            <p style={{ margin: 0, fontSize: '0.9rem', color: '#74788d' }}>
              {widget.description}
            </p>

            {snapshot.isDragging && (
              <div style={{
                position: 'absolute',
                bottom: '8px',
                right: '8px',
                backgroundColor: 'var(--theme-accent)',
                color: 'white',
                borderRadius: '4px',
                padding: '2px 6px',
                fontSize: '0.75rem',
                fontWeight: 'bold'
              }}>
                Dragging
              </div>
            )}
          </div>
        )}
      </Draggable>
    );
  };

  // Apply theme, content style, and control type on mount
  useEffect(() => {
    const savedTheme = localStorage.getItem("appTheme") || "default";
    const savedContentStyle = localStorage.getItem("contentStyle") || "light";
    const savedControlType = localStorage.getItem("controlType") || "analog";
    document.documentElement.setAttribute("data-theme", savedTheme);
    document.documentElement.setAttribute("data-content-style", savedContentStyle);
    setControlType(savedControlType);
  }, []);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Pokemon Radiance" breadcrumbItem="Settings" />

          {showPopup && (
            <Alert
              color={popupColor}
              className="position-fixed top-0 start-50 translate-middle-x mt-3 shadow"
              toggle={() => setShowPopup(false)}
              fade
              style={{ zIndex: 9999, minWidth: "300px" }}
            >
              {popupMessage}
            </Alert>
          )}

          {/* Discord Authentication Section */}
          <Row className="mb-4">
            <Col lg={6}>
              <Card className="shadow-sm">
                <CardBody>
                  <CardTitle tag="h5">Discord Authentication</CardTitle>
                  <CardSubtitle className="mb-3 text-muted">
                    Link or unlink your Discord account.
                  </CardSubtitle>

                  {discordUser ? (
                    <>
                      <div className="mb-3">
                        Connected as{" "}
                        <strong>
                          {discordUser.username}#{discordUser.discriminator}
                        </strong>
                      </div>
                      <Button color="danger" onClick={handleDiscordDisconnect}>
                        Disconnect Discord
                      </Button>
                    </>
                  ) : (
                    <Button color="primary" onClick={handleDiscordConnect}>
                      Connect Discord
                    </Button>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Update Email & Password */}
          <Row>
            <Col lg={6}>
              <Card className="shadow-sm">
                <CardBody>
                  <CardTitle tag="h5">Update Email</CardTitle>
                  <CardSubtitle className="mb-3 text-muted">
                    Re-authenticate with your password to update your email.
                  </CardSubtitle>
                  <Form onSubmit={emailValidation.handleSubmit}>
                    <FormGroup className="mb-3">
                      <Label>New Email</Label>
                      <Input
                        type="email"
                        name="new_email"
                        placeholder="Enter new email"
                        onChange={emailValidation.handleChange}
                        onBlur={emailValidation.handleBlur}
                        value={emailValidation.values.new_email}
                        invalid={
                          emailValidation.touched.new_email &&
                          !!emailValidation.errors.new_email
                        }
                      />
                      <FormFeedback>
                        {emailValidation.errors.new_email}
                      </FormFeedback>
                    </FormGroup>
                    <FormGroup className="mb-3">
                      <Label>Current Password</Label>
                      <Input
                        type="password"
                        name="password"
                        placeholder="Enter current password"
                        onChange={emailValidation.handleChange}
                        onBlur={emailValidation.handleBlur}
                        value={emailValidation.values.password}
                        invalid={
                          emailValidation.touched.password &&
                          !!emailValidation.errors.password
                        }
                      />
                      <FormFeedback>
                        {emailValidation.errors.password}
                      </FormFeedback>
                    </FormGroup>
                    <div className="text-end">
                      <Button type="submit" color="primary">
                        Update Email
                      </Button>
                    </div>
                  </Form>
                </CardBody>
              </Card>
            </Col>

            <Col lg={6}>
              <Card className="shadow-sm">
                <CardBody>
                  <CardTitle tag="h5">Update Password</CardTitle>
                  <CardSubtitle className="mb-3 text-muted">
                    Your new password must follow our policy and not match your
                    current one.
                  </CardSubtitle>
                  <Form onSubmit={passwordValidation.handleSubmit}>
                    <FormGroup className="mb-3">
                      <Label>Current Password</Label>
                      <Input
                        type="password"
                        name="old_password"
                        placeholder="Enter current password"
                        onChange={passwordValidation.handleChange}
                        onBlur={passwordValidation.handleBlur}
                        value={passwordValidation.values.old_password}
                        invalid={
                          passwordValidation.touched.old_password &&
                          !!passwordValidation.errors.old_password
                        }
                      />
                      <FormFeedback>
                        {passwordValidation.errors.old_password}
                      </FormFeedback>
                    </FormGroup>
                    <FormGroup className="mb-3">
                      <Label>New Password</Label>
                      <Input
                        type="password"
                        name="new_password"
                        placeholder="Enter new password"
                        onChange={passwordValidation.handleChange}
                        onBlur={passwordValidation.handleBlur}
                        value={passwordValidation.values.new_password}
                        invalid={
                          passwordValidation.touched.new_password &&
                          !!passwordValidation.errors.new_password
                        }
                      />
                      <FormFeedback>
                        {passwordValidation.errors.new_password}
                      </FormFeedback>
                    </FormGroup>
                    <FormGroup className="mb-3">
                      <Label>Confirm New Password</Label>
                      <Input
                        type="password"
                        name="confirm_password"
                        placeholder="Repeat new password"
                        onChange={passwordValidation.handleChange}
                        onBlur={passwordValidation.handleBlur}
                        value={passwordValidation.values.confirm_password}
                        invalid={
                          passwordValidation.touched.confirm_password &&
                          !!passwordValidation.errors.confirm_password
                        }
                      />
                      <FormFeedback>
                        {passwordValidation.errors.confirm_password}
                      </FormFeedback>
                    </FormGroup>
                    <div className="text-end">
                      <Button type="submit" color="primary">
                        Update Password
                      </Button>
                    </div>
                  </Form>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Theme Selection Section */}
          <Row className="mt-5">
            <Col lg={12}>
              <Card className="shadow-sm">
                <CardBody>
                  <CardTitle tag="h5">Appearance Theme</CardTitle>
                  <CardSubtitle className="mb-4 text-muted">
                    Customize the appearance of your header, sidebar, and menu.
                    Choose from professionally designed themes below.
                  </CardSubtitle>

                  <div className="theme-grid">
                    {themeOptions.map((theme) => (
                      <div
                        key={theme.id}
                        className={`theme-card ${
                          selectedTheme === theme.id ? "theme-card-selected" : ""
                        }`}
                        onClick={() => handleThemeUpdate(theme.id)}
                      >
                        <div className="theme-preview">
                          <div
                            className="theme-preview-header"
                            style={{ backgroundColor: theme.preview.header }}
                          >
                            <div
                              className="theme-preview-accent"
                              style={{ backgroundColor: theme.preview.accent }}
                            />
                          </div>
                          <div className="theme-preview-body">
                            <div
                              className="theme-preview-sidebar"
                              style={{ backgroundColor: theme.preview.sidebar }}
                            />
                            <div 
                              className="theme-preview-content"
                              style={{ backgroundColor: theme.preview.content }}
                            >
                              <div 
                                className="theme-preview-card"
                                style={{ backgroundColor: theme.preview.card }}
                              />
                            </div>
                          </div>
                          <div 
                            className="theme-preview-footer"
                            style={{ backgroundColor: theme.preview.sidebar }}
                          />
                        </div>
                        <div className="theme-info">
                          <h6 className="theme-name">{theme.name}</h6>
                          <p className="theme-description">{theme.description}</p>
                        </div>
                        {selectedTheme === theme.id && (
                          <div className="theme-selected-badge">
                            <i className="ri-check-line" /> Active
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Content Style Section */}
          <Row className="mt-4">
            <Col lg={12}>
              <Card className="shadow-sm">
                <CardBody>
                  <CardTitle tag="h5">Page Content Style</CardTitle>
                  <CardSubtitle className="mb-4 text-muted">
                    Customize the appearance of page backgrounds, cards, and content areas.
                  </CardSubtitle>

                  <div className="content-style-grid">
                    {contentStyleOptions.map((style) => (
                      <div
                        key={style.id}
                        className={`content-style-card ${
                          selectedContentStyle === style.id ? "content-style-card-selected" : ""
                        }`}
                        onClick={() => handleContentStyleUpdate(style.id)}
                      >
                        <div className="content-style-preview">
                          <div
                            className="content-style-preview-bg"
                            style={{ backgroundColor: style.preview.background }}
                          >
                            <div
                              className="content-style-preview-card"
                              style={{ 
                                backgroundColor: style.preview.card,
                                color: style.preview.text 
                              }}
                            >
                              <div className="preview-text-line" />
                              <div className="preview-text-line short" />
                            </div>
                          </div>
                        </div>
                        <div className="content-style-info">
                          <h6 className="content-style-name">{style.name}</h6>
                          <p className="content-style-description">{style.description}</p>
                        </div>
                        {selectedContentStyle === style.id && (
                          <div className="content-style-selected-badge">
                            <i className="ri-check-line" /> Active
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Control Type Section */}
          <Row className="mt-4">
            <Col lg={12}>
              <Card className="shadow-sm">
                <CardBody>
                  <CardTitle tag="h5">Movement Controls</CardTitle>
                  <CardSubtitle className="mb-4 text-muted">
                    Choose your preferred control style for map navigation
                  </CardSubtitle>

                  <div className="control-type-selector">
                    {/* Analog Stick Option */}
                    <div
                      className={`control-option ${controlType === 'analog' ? 'control-option-selected' : ''}`}
                      onClick={() => handleControlTypeUpdate('analog')}
                    >
                      <div className="control-preview">
                        <div className="analog-preview">
                          <div className="analog-preview-base">
                            <div className="analog-preview-knob"></div>
                          </div>
                        </div>
                      </div>
                      <div className="control-info">
                        <h6 className="control-name">Analog Stick</h6>
                        <p className="control-description">
                          360° movement with smooth directional control. GameCube-style analog stick.
                        </p>
                      </div>
                      {controlType === 'analog' && (
                        <div className="control-selected-badge">
                          <i className="ri-check-line" /> Active
                        </div>
                      )}
                    </div>

                    {/* D-Pad Option */}
                    <div
                      className={`control-option ${controlType === 'dpad' ? 'control-option-selected' : ''}`}
                      onClick={() => handleControlTypeUpdate('dpad')}
                    >
                      <div className="control-preview">
                        <div className="dpad-preview">
                          <div className="dpad-preview-center"></div>
                          <div className="dpad-preview-up">▲</div>
                          <div className="dpad-preview-right">▶</div>
                          <div className="dpad-preview-down">▼</div>
                          <div className="dpad-preview-left">◀</div>
                        </div>
                      </div>
                      <div className="control-info">
                        <h6 className="control-name">D-Pad</h6>
                        <p className="control-description">
                          Classic 4-directional control. Game Boy-style directional pad for precise movement.
                        </p>
                      </div>
                      {controlType === 'dpad' && (
                        <div className="control-selected-badge">
                          <i className="ri-check-line" /> Active
                        </div>
                      )}
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Dashboard Widgets Section */}
          <Row className="mt-4">
            <Col lg={12}>
              <Card className="shadow-sm">
                <CardBody>
                  <CardTitle tag="h5">Dashboard Widgets</CardTitle>
                  <CardSubtitle className="mb-4 text-muted">
                    Drag to reorder. Click to toggle on/off.
                  </CardSubtitle>

                  <DragDropContext onDragEnd={handleOnDragEnd}>
                    <Droppable droppableId="widgets" type="WIDGET">
                      {(provided, snapshot) => (
                        <div
                          {...provided.droppableProps}
                          ref={provided.innerRef}
                          style={{
                            backgroundColor: snapshot.isDraggingOver ? 'rgba(var(--theme-accent-rgb), 0.05)' : 'transparent',
                            borderRadius: '8px',
                            padding: '8px',
                            minHeight: '200px',
                            position: 'relative'
                          }}
                        >
                          {widgetOrder.map((widgetKey, index) => 
                            renderWidgetCard(widgetKey, index)
                          )}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>

                  {/* Widget Ordering Info */}
                  <div style={{ marginTop: '20px', padding: '15px', backgroundColor: 'rgba(var(--theme-accent-rgb), 0.1)', borderRadius: '8px', border: '1px solid var(--theme-accent)' }}>
                    <p style={{ margin: 0, fontSize: '0.9rem', color: '#74788d' }}>
                      <i className="ri-drag-move-line" style={{ marginRight: '8px', color: 'var(--theme-accent)' }} />
                      Drag widgets to reorder them. The position here determines the order on your dashboard. Click the toggle to enable/disable widgets.
                    </p>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Avatar Section */}
          <Row className="mt-5">
            <Col lg={12}>
              <CardTitle tag="h5">Update Avatar</CardTitle>
              <CardSubtitle className="mb-3 text-muted">
                Choose an avatar below:
              </CardSubtitle>

              {selectedAvatar !== null && (
                <div className="text-center mb-4">
                  <img
                    src={`/avatars/${selectedAvatar}.png`}
                    alt="Selected avatar"
                    style={{
                      width: "150px",
                      height: "150px",
                      border: "3px solid #4CAF50",
                      borderRadius: "10px",
                    }}
                  />
                  <div className="mt-2 fw-bold">Selected Avatar</div>
                </div>
              )}
              <Card className="shadow-sm" style={{ maxHeight: "300px", overflowY: "auto" }}>
                <CardBody>
                  <div className="d-flex flex-wrap justify-content-start gap-3">
                    {avatarOptions.map((avatarId) => (
                      <div key={avatarId} style={{ textAlign: "center" }}>
                        <img
                          src={`/avatars/${avatarId}.png`}
                          alt={`Avatar ${avatarId}`}
                          onClick={() => setSelectedAvatar(avatarId)}
                          style={{
                            border:
                              selectedAvatar === avatarId
                                ? "3px solid #4CAF50"
                                : "2px solid #ccc",
                            borderRadius: "10px",
                            cursor: "pointer",
                            width: "100px",
                            height: "100px",
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </CardBody>
              </Card>
              <div className="text-end mt-3">
                <Button color="primary" onClick={handleAvatarUpdate}>
                  Update Avatar
                </Button>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default Settings;
